import { RefObject } from 'react';

import { requestAnimation } from '@hh.ru/magritte-common-func-utils';
import { ScrollGestureEvent, ScrollGestureHandler } from '@hh.ru/magritte-internal-custom-scroll/types';

// Минимальная скорость скролла для анимации инерции (пикселей в мс)
const MIN_SPEED_TO_ANIMATE = 0.01;
// Минимальный сдвиг контейнера позиционирования в пикселях для анимации инерции
const MIN_DELTA_TO_ANIMATE = 5;
// Ускорение скролла при отпускании пальца, оно будет направленно в сторону противоположную движению контейнера
// в пикселях в мс
// Используется для анимации инерции при тач скролле, чем оно больше, тем быстрее будет прекращаться движение по инерции
const VELOCITY = 0.006;

/**
 * Возвращает значение из диапазона [min, max], если value в этом диапазоне, то возвращает его, если нет, то ближайшее
 * значени из диапазона [min, max]
 * @param value Исходное значение
 * @param min Нижняя граница диапазона
 * @param max Верхняя граница диапазона
 * @returns Значение из диапазона [min, max]
 */
export const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

/**
 * Инициализирует обработчики тач событий для скролла на мобильных устройствах
 * @param axis ось скролла
 * @param wrapperRef реф со скроллящимся элементом
 * @param onTouchMove обработчик события инерциального скролла, который должен применять translate:transform
 * @param onTouchEnd обработчик touchend, либо завершения inertial анимации
 * @param onWheelMove обработчик события скролла мышью, который должен применять translate:transform
 * @returns Функция удаляющая добавленные обработчики событий
 */

interface InitScrollHandlersProps {
    axis: 'horizontal' | 'vertical';
    wrapperRef: RefObject<HTMLElement>;
    onTouchMove: ScrollGestureHandler;
    onTouchEnd?: VoidFunction;
    onWheelMove?: ScrollGestureHandler;
}

export const initScrollHandlers = ({
    axis,
    wrapperRef,
    onTouchMove,
    onTouchEnd,
    onWheelMove,
}: InitScrollHandlersProps): VoidFunction => {
    let prevTime = 0;
    let prevClientX = 0;
    let prevClientY = 0;
    let dragSpeed = 0;
    let coordinateDelta = 0;
    let prevTranslationDelta = 0;
    let startedOnMe = false;
    let firstMove = true;

    const runInertialAnimation = requestAnimation((_, initialSpeed: number, animationStartTime: number) => {
        if (startedOnMe) {
            return;
        }

        const timeDelta = Date.now() - animationStartTime;
        // ускорение должно быть направлено в противоположную к скроллу сторону
        const velocity = initialSpeed < 0 ? VELOCITY : -VELOCITY;

        // формулы для вычисления пройденного пути и скорости в момент времени t для равноускоренного движения
        // delta относительно стартовой позиции контейнера в момент начала анимации инерции
        const translationDelta = initialSpeed * timeDelta + (velocity * (timeDelta * timeDelta)) / 2;
        const currentSpeed = initialSpeed + velocity * timeDelta;
        const isSignChanged = (initialSpeed > 0 && currentSpeed < 0) || (initialSpeed < 0 && currentSpeed > 0);

        let defaultPrevented = false;
        const event: ScrollGestureEvent = {
            // delta относительно позиции контейнера на предыдущем фрейме анимации
            delta: translationDelta - prevTranslationDelta,
            preventDefault: () => (defaultPrevented = true),
        };
        prevTranslationDelta = translationDelta;
        onTouchMove(event);
        if (Math.abs(currentSpeed) < MIN_SPEED_TO_ANIMATE || isSignChanged || defaultPrevented) {
            prevTranslationDelta = 0;
            onTouchEnd?.();
            return;
        }

        runInertialAnimation(initialSpeed, animationStartTime);
    });

    const handleTouchMove = (touchEvent: TouchEvent) => {
        if (
            !wrapperRef.current ||
            // Если "пальцев" больше одного, то это не скролл
            touchEvent.touches.length > 1 ||
            !startedOnMe
        ) {
            return;
        }

        const { clientX, clientY } = touchEvent.touches[0];
        const clientXDelta = Math.abs(clientX - prevClientX);
        const clientYDelta = Math.abs(clientY - prevClientY);
        if (
            firstMove &&
            // Если дельта по cross-axis больше чем дельта по main axis,
            // то скорее всего пользователь хочет скроллить страницу по cross-axis
            ((axis === 'horizontal' && clientYDelta > clientXDelta) ||
                (axis === 'vertical' && clientXDelta > clientYDelta))
        ) {
            startedOnMe = false;
            wrapperRef.current.removeEventListener('touchmove', handleTouchMove);
            return;
        }

        firstMove = false;
        const now = Date.now();
        const timeDelta = now - prevTime;
        coordinateDelta = axis === 'horizontal' ? prevClientX - clientX : prevClientY - clientY;
        dragSpeed = -coordinateDelta / timeDelta;
        prevClientX = clientX;
        prevClientY = clientY;
        prevTime = now;
        onTouchMove({ delta: -coordinateDelta, preventDefault: () => {} }); // eslint-disable-line @typescript-eslint/no-empty-function
    };

    const handleTouchStart = (touchEvent: TouchEvent) => {
        if (!wrapperRef.current || touchEvent.touches.length > 1) {
            return;
        }

        firstMove = true;
        startedOnMe = true;
        const { clientX, clientY } = touchEvent.touches[0];
        prevClientX = clientX;
        prevClientY = clientY;
        prevTime = Date.now();
        coordinateDelta = 0;
        wrapperRef.current.addEventListener('touchmove', handleTouchMove);
    };

    const handleTouchEnd = () => {
        if (!wrapperRef.current || !startedOnMe) {
            return;
        }

        startedOnMe = false;
        wrapperRef.current.removeEventListener('touchmove', handleTouchMove);
        const initialSpeed = clamp(dragSpeed, -3, 3);
        if (Math.abs(coordinateDelta) > MIN_DELTA_TO_ANIMATE && Math.abs(initialSpeed) >= MIN_SPEED_TO_ANIMATE) {
            runInertialAnimation(initialSpeed, Date.now());
        } else {
            onTouchEnd?.();
        }
    };

    const wheelHandler = (event: WheelEvent) => {
        if (!wrapperRef.current || !onWheelMove) {
            return;
        }
        let defaultPrevented = false;
        const delta = Math.abs(event.deltaY) > Math.abs(event.deltaX) ? -event.deltaY : -event.deltaX;
        onWheelMove({ delta, preventDefault: () => (defaultPrevented = true) });
        if (defaultPrevented) {
            event.preventDefault();
        }
    };

    wrapperRef.current?.addEventListener('touchstart', handleTouchStart);
    wrapperRef.current?.addEventListener('wheel', wheelHandler, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
        wrapperRef.current?.removeEventListener('touchstart', handleTouchStart);
        wrapperRef.current?.removeEventListener('wheel', wheelHandler);
        document.removeEventListener('touchend', handleTouchEnd);
    };
};
